import { Check, User } from 'lucide-react'
import { useState } from 'react'
import { Avatar } from '../../components/Avatar'
import { Button } from '../../components/Button'
import { Modal } from '../../components/Modal'
import { useAppData } from '../../context/AppDataContext'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../../context/ToastContext'
import { hasFullVisibility } from '../../hooks/useScopedWork'
import { CONTENT_TYPES, PLATFORMS } from '../../data/types'
import type { ContentPlatform, ContentType } from '../../data/types'
import { Select } from '../../components/Select'
import { DatePicker } from '../../components/DatePicker'

/**
 * Guided replacement for the old "Add Content" behaviour, which dropped an
 * "Untitled content" row straight into the spreadsheet for somebody to
 * inline-edit across a dozen columns. This asks for only what actually
 * matters to get a piece on the calendar — title, client, format, assignee,
 * posting date — and lets the spreadsheet stay the tool for everything else
 * (captions, hooks, approvals) after it exists.
 *
 * ## What is ours rather than the design's
 *
 * The vocabulary lists come from `data/types.ts` rather than a mock-data
 * module, and the two dates are separate fields because this platform keeps
 * them separate: `due` is when the studio has to have finished, `scheduledFor`
 * is when it goes out, and the gap between them is the only buffer anyone has.
 * The design had one "due date" that meant whichever of the two the reader
 * assumed. Getting that wrong in a create form is how a reel is "due" the
 * morning it is meant to be live.
 *
 * The posting date defaults to nothing rather than to a fixed day. The design
 * shipped `'2026-09-15'` hardcoded, which is fine in a demo and would quietly
 * schedule a month of work onto one afternoon here.
 */
export function NewContentModal({
  open,
  onClose,
  defaultClientId,
}: {
  open: boolean
  onClose: () => void
  defaultClientId?: string | undefined
}) {
  const { addContent, currentClients, directory } = useAppData()
  const { currentUser } = useAuth()
  const { showToast } = useToast()
  const canAssignOthers = hasFullVisibility(currentUser?.role)

  const [title, setTitle] = useState('')
  const [clientId, setClientId] = useState(defaultClientId ?? currentClients[0]?.id ?? '')
  const [platform, setPlatform] = useState<ContentPlatform>(PLATFORMS[0] ?? 'Instagram')
  const [contentType, setContentType] = useState<ContentType>(CONTENT_TYPES[0] ?? 'Reel')
  const [topic, setTopic] = useState('')
  const [assignedToId, setAssignedToId] = useState(
    currentUser?.linkedPersonaId ?? directory[0]?.id ?? '',
  )
  const [date, setDate] = useState('')
  const [submitting, setSubmitting] = useState(false)

  /*
   * Somebody without full visibility can only put their own name on it — and
   * the server pins the owner to their persona regardless of what is sent, so
   * showing them a roster they cannot pick from would be offering a choice
   * that is not theirs. They see their own row, and it is not a button.
   */
  const assigneeOptions = canAssignOthers
    ? directory
    : directory.filter((m) => m.id === assignedToId)

  function reset() {
    setTitle('')
    setTopic('')
    setPlatform(PLATFORMS[0] ?? 'Instagram')
    setContentType(CONTENT_TYPES[0] ?? 'Reel')
    setDate('')
  }

  function handleClose() {
    reset()
    onClose()
  }

  async function handleCreate() {
    const trimmed = title.trim()
    if (trimmed === '') return
    const client = clientId === '' ? currentClients[0]?.id : clientId
    if (client === undefined) return

    setSubmitting(true)
    try {
      await addContent({
        clientId: client,
        title: trimmed,
        platform,
        contentType,
        topic: topic.trim(),
        assignedToId,
        date,
      })
      showToast('Added to the content plan.')
      handleClose()
    } catch (err) {
      // The context reloads on success and throws on failure; without this the
      // modal would close on a request that never landed, and the piece would
      // be missing with nothing said about it.
      showToast(err instanceof Error ? err.message : 'Could not add it.', 'warning')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="New content"
      description="Caption, hook and approvals come later — this is just enough to put it on the plan."
      footer={
        <>
          <Button variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={submitting || title.trim() === ''}>
            {submitting ? 'Adding…' : 'Add it'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <label className="field-label" htmlFor="content-title">
            Title
          </label>
          <input
            id="content-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. What the rain does to your skin"
            className="field-input"
          />
        </div>

        <div>
          <label className="field-label" htmlFor="content-topic">
            Topic <span className="font-normal text-shell-text/70">(optional)</span>
          </label>
          <input
            id="content-topic"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder="What is this one about?"
            className="field-input"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label" htmlFor="content-client">
              Client
            </label>
            <Select
              id="content-client"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              className="field-input"
            >
              {currentClients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="field-label" htmlFor="content-date">
              Goes out <span className="font-normal text-shell-text/70">(optional)</span>
            </label>
            <DatePicker
              id="content-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              placeholder="Not scheduled"
              className="field-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="field-label" htmlFor="content-platform">
              Platform
            </label>
            <Select
              id="content-platform"
              value={platform}
              onChange={(e) => setPlatform(e.target.value as ContentPlatform)}
              className="field-input"
            >
              {PLATFORMS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <label className="field-label" htmlFor="content-type">
              Format
            </label>
            <Select
              id="content-type"
              value={contentType}
              onChange={(e) => setContentType(e.target.value as ContentType)}
              className="field-input"
            >
              {CONTENT_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Select>
          </div>
        </div>

        <div>
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-shell-text">
            <User size={12} /> Whose is it
          </p>
          <div className="grid grid-cols-2 gap-2">
            {assigneeOptions.map((m) => (
              <button
                key={m.id}
                type="button"
                disabled={!canAssignOthers}
                onClick={() => canAssignOthers && setAssignedToId(m.id)}
                className={`flex items-center gap-2 rounded-xl border px-2.5 py-2 text-left text-sm transition-colors ${
                  assignedToId === m.id ? 'border-brand-300 bg-brand-50 text-brand-700' : 'border-shell-border text-shell-text hover:bg-shell-border/40'
                } ${!canAssignOthers ? 'cursor-default' : ''}`}
              >
                <Avatar name={m.name} color={m.color} size="xs" />
                <span className="truncate">{m.name}</span>
                {assignedToId === m.id && <Check size={13} className="ml-auto shrink-0" />}
              </button>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  )
}

